// Utility functions for getting document details in Microsoft Word using Office JavaScript API

export interface DocumentInfo {
  name: string;
  url?: string;
  size: number; // in bytes
  id?: string;
}

// Cache to prevent multiple ID generation in the same session
let cachedDocumentId: string | null = null;
let documentIdPromise: Promise<string> | null = null;

/**
 * Generate a unique GUID for document identification
 */
const generateGUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Get or create a unique document ID stored in custom properties
 * This provides a stable identifier that persists across sessions and renames
 * Uses caching to prevent race conditions when called multiple times
 */
const getOrCreateDocumentId = async (context: Word.RequestContext): Promise<string> => {
  // If we already have a cached ID, return it immediately
  if (cachedDocumentId) {
    return cachedDocumentId;
  }

  // If there's already a pending request, wait for it
  if (documentIdPromise) {
    return documentIdPromise;
  }

  // Create a new promise for getting/creating the document ID
  documentIdPromise = (async () => {
    try {
      const customProperties = context.document.properties.customProperties;

      // Get the specific property as an "OrNullObject" which prevents "undefined" errors
      const docIdProp = customProperties.getItemOrNullObject("aila_document_id");

      // Load both the value and the isNullObject check
      docIdProp.load(["value", "isNullObject"]);
      await context.sync();

      let documentId: string;

      if (!docIdProp.isNullObject) {
        // Property exists, safe to access value
        documentId = docIdProp.value;
      } else {
        // Create new document ID if it doesn't exist
        documentId = generateGUID();
        customProperties.add("aila_document_id", documentId);
        context.document.save();
        await context.sync();
      }

      // Cache the ID for future calls
      cachedDocumentId = documentId;

      return documentId;
    } catch (error) {
      console.error("Error getting or creating document ID:", error);
      throw error;
    } finally {
      // Clear the promise since we're done (success or failure)
      documentIdPromise = null;
    }
  })();

  return documentIdPromise;
};

/**
 * Get document information including name, URL, and size
 */
export const getDocumentInfo = (): Promise<DocumentInfo> => {
  return new Promise((resolve, reject) => {
    try {
      Word.run(async (context) => {
        try {
          const body = context.document.body;
          body.load(["text"]);

          // Get or create unique document ID from custom properties
          const documentId = await getOrCreateDocumentId(context);

          await context.sync();

          // Get document size by calculating text length
          const documentText = body.text;
          const sizeInBytes = new Blob([documentText]).size;

          // Get document URL if available
          const documentUrl = Office.context.document.url;

          // Get document name from URL filename
          let documentName = "Untitled Document";
          if (documentUrl) {
            const urlParts = documentUrl.split("/");
            const filename = urlParts[urlParts.length - 1];
            if (filename) {
              documentName = decodeURIComponent(filename);
            }
          }

          const documentInfo: DocumentInfo = {
            name: documentName,
            url: documentUrl,
            size: sizeInBytes,
            id: documentId,
          };

          resolve(documentInfo);
        } catch (error) {
          reject(new Error(`Failed to get document info: ${error}`));
        }
      });
    } catch (error) {
      reject(new Error(`Failed to access Word API: ${error}`));
    }
  });
};

/**
 * Set up event listener for document content changes
 * Uses both onParagraphChanged and onParagraphInserted to catch all content changes including paste
 */
export const setupDocumentChangeListener = async (
  callback?: () => void
): Promise<() => Promise<void>> => {
  try {
    // Check if the API version is supported
    if (!Office.context.requirements.isSetSupported("WordApi", "1.6")) {
      return async () => {};
    }

    let changedHandler: OfficeExtension.EventHandlerResult<Word.ParagraphChangedEventArgs> | null =
      null;
    let insertedHandler: OfficeExtension.EventHandlerResult<Word.ParagraphAddedEventArgs> | null =
      null;
    let deletedHandler: OfficeExtension.EventHandlerResult<Word.ParagraphDeletedEventArgs> | null =
      null;

    await Word.run(async (context) => {
      // Listen for paragraph changes (edits to existing content)
      changedHandler = context.document.onParagraphChanged.add(async () => {
        if (callback) {
          callback();
        }
      });

      // Listen for paragraph additions (paste, new paragraphs)
      insertedHandler = context.document.onParagraphAdded.add(async () => {
        if (callback) {
          callback();
        }
      });

      // Listen for paragraph deletions (cut, delete)
      deletedHandler = context.document.onParagraphDeleted.add(async () => {
        if (callback) {
          callback();
        }
      });

      await context.sync();
    });

    // Return cleanup function
    return async () => {
      await Word.run(async (context) => {
        if (changedHandler) {
          changedHandler.remove();
        }
        if (insertedHandler) {
          insertedHandler.remove();
        }
        if (deletedHandler) {
          deletedHandler.remove();
        }
        await context.sync();
      });
    };
  } catch (error) {
    console.error("Error setting up document listener:", error);
    return async () => {};
  }
};
